// src/features/payments/pages/PaymentsList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";
import { Download } from "lucide-react";

import {
  listPayments,
  getPaymentsSummary,
  exportPayments,
} from "../api";

import { listVenues } from "../../../pages/SuperAdmin/Venues/venuesApi";

const fmt = new Intl.NumberFormat("en-IN");
const money = (v) => `₹${fmt.format(v || 0)}`;

const percent = (v) => `${Number(v || 0)}%`;

// Slot value (fallback safe)
const slotValue = (p) =>
  p?.slotValue ??
  p?.slot_amount ??
  p?.grossAmount ??
  p?.totalAmount ?? // ✅ slot price gross (after backend fix)
  0;

// Commission percent (fallback safe)
const commissionPercent = (p) =>
  p?.commissionPercent ??
  p?.commission_percent ??
  p?.commissionPct ??
  0;

// Promo discount amount (fallback safe)
// Promo (code + discount) — Payment model is source of truth
const promoCell = (p) => {
  const code = String(
    p?.promoCode ??            // ✅ Payment snapshot field (RIGHT)
    p?.promo_code ??           // fallback (if API mixes reservation)
    ""
  ).trim();

  const discount = Number(
    p?.promoDiscountAmount ??  // ✅ Payment snapshot field (RIGHT)
    p?.discount_amount ??      // fallback (if API mixes reservation)
    0
  ) || 0;

  if (!code && discount <= 0) return "—";
  if (code && discount > 0) return `${code} (−${money(discount)})`;
  if (code) return code;
  return `−${money(discount)}`;
};

// Platform Net (fallback safe)
// If backend already sends netCommission use that else calculate commission - gateway (only for non-venue-admin view)
const platformNet = (p) =>
  p?.netCommission ?? Math.max(Number(p?.commissionAmount || 0) - Number(p?.gatewayFee || 0), 0);

const PaymentsList = () => {
  // ---- ctx (venue admin detection) ----
  const ctx = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("va_ctx") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isVenueAdmin = String(ctx.role || "").toLowerCase() === "venue_admin";

  // ✅ resolve "logged in venue" (prefer ctx.venueId, else first ctx.venues)
  const loggedInVenueId = useMemo(() => {
    const vid = ctx.venueId || ctx.venue || "";
    if (vid) return String(vid);

    const arr = Array.isArray(ctx.venues) ? ctx.venues : [];
    if (arr.length) return String(arr[0]);

    return "";
  }, [ctx]);

  // ---- filters ----
  const [status, setStatus] = useState("");
  const [month, setMonth] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");

  // ✅ venueId:
  // - venue_admin: fixed to loggedInVenueId
  // - others: "" means all venues
  const [venueId, setVenueId] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem("va_ctx") || "{}");
      if (String(c.role || "").toLowerCase() === "venue_admin") {
        return String(c.venueId || (Array.isArray(c.venues) ? c.venues[0] : "") || "");
      }
    } catch (_) { }
    return "";
  });

  // ---- paging ----
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // ---- data ----
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // ---- KPIs ----
  const [kpi, setKpi] = useState({
    count: 0,
    totalAmount: 0,
    commission: 0,
    gatewayFee: 0,
    venueSettlement: 0,
  });

  // ---- venues dropdown ----
  const [venueOptions, setVenueOptions] = useState([]);

  // ✅ keep venueId in sync if user is venue admin
  useEffect(() => {
    if (isVenueAdmin && loggedInVenueId && venueId !== loggedInVenueId) {
      setVenueId(loggedInVenueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVenueAdmin, loggedInVenueId]);

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      // ✅ For venue_admin: DO NOT send venueId at all (backend already scopes)
      // ✅ For others: allow venueId filter
      const params = {
        page: opts.page ?? page,
        limit: opts.limit ?? limit,
        status,
        month,
        dateFrom,
        dateTo,
        q,
        ...(isVenueAdmin ? {} : { venueId }),
      };

      Object.keys(params).forEach(
        (k) => (params[k] === "" || params[k] == null) && delete params[k]
      );

      const listRes = await listPayments(params);
      setItems(listRes.items || []);
      setPages(listRes.pages || 1);
      setTotal(listRes.total || 0);
      setPage(listRes.page || 1);
      setLimit(listRes.limit || limit);

      const ks = await getPaymentsSummary(params);
      setKpi(ks || {});
    } catch (e) {
      console.error(e);
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const loadVenues = async () => {
    try {
      const res = await listVenues({ page: 1, pageSize: 500, q: "" });
      const items = res.items || [];

      const mapped = items.map((v) => ({
        id: v._id || v.id,
        name: v.location?.city ? `${v.name} – ${v.location.city}` : v.name,
      }));

      // ✅ For venue_admin: only keep their venue in options
      if (isVenueAdmin) {
        const onlyMine = mapped.filter((v) => String(v.id) === String(loggedInVenueId));
        setVenueOptions(onlyMine);
      } else {
        setVenueOptions(mapped);
      }
    } catch (e) {
      console.error("loadVenues error", e);
    }
  };

  useEffect(() => {
    loadVenues();
    load({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onExport = async (format) => {
    try {
      // ✅ For venue_admin: do not include venueId in export params
      const params = {
        page,
        limit,
        status,
        month,
        dateFrom,
        dateTo,
        q,
        ...(isVenueAdmin ? {} : { venueId }),
        format,
      };

      Object.keys(params).forEach(
        (k) => (params[k] === "" || params[k] == null) && delete params[k]
      );

      await exportPayments(params);
      toast.success(`Export (${format.toUpperCase()}) started`);
    } catch {
      toast.error("Export failed");
    }
  };

  const currentVenueLabel = useMemo(() => {
    if (!isVenueAdmin) return "";
    return (
      venueOptions.find((v) => String(v.id) === String(loggedInVenueId))?.name ||
      loggedInVenueId ||
      "Current venue"
    );
  }, [isVenueAdmin, venueOptions, loggedInVenueId]);

  return (
    <div className="payments-skin">
      <style>{styles}</style>
      <div className="sa-page">
        <div className="sa-head-row">
          <h1 className="sa-h1 sa-topbar-managed-page-title">Payments</h1>
        </div>

        <div className="sa-card">
          <div className="sa-row sa-wrap">
            <div className="sa-field">
              <label>Venue</label>

              {isVenueAdmin ? (
                // ✅ Venue admin: show only their venue, preselected & non-editable
                <input type="text" value={currentVenueLabel} disabled />
              ) : (
                // ✅ Others: full dropdown (All + all venues)
                <select value={venueId} onChange={(e) => setVenueId(e.target.value)}>
                  <option value="">All Venues</option>
                  {venueOptions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name || v.id}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="sa-field">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>
                <option value="completed">Success</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="sa-field">
              <label>Month</label>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>

            <div className="sa-field">
              <label>From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>

            <div className="sa-field">
              <label>To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>

            <div className="sa-actions">
              <button className="sa-btn" onClick={() => load({ page: 1 })}>
                {loading ? "Loading…" : "Apply"}
              </button>
              <button className="sa-btn-outline" onClick={() => onExport("csv")}>
                <Download className="lucide" /> Export CSV
              </button>
            </div>
          </div>
        </div>

        <div className="sa-kpis">
          <div className="sa-kpi">
            <div className="sa-kpi-title">Total Collected</div>
            <div className="sa-kpi-value">{money(kpi.totalAmount || 0)}</div>
          </div>
          <div className="sa-kpi">
            <div className="sa-kpi-title">
              {isVenueAdmin ? "Total Platform Fee" : "Total Commission"}
            </div>

            <div className="sa-kpi-value">{money(kpi.commission || 0)}</div>
          </div>

          <div className="sa-kpi">
            <div className="sa-kpi-title">Total Payable to Venues</div>
            <div className="sa-kpi-value">{money(kpi.venueSettlement || 0)}</div>
          </div>
        </div>

        <div className="table-toolbar">
          <TableToolbar
            searchTerm={q}
            onSearchChange={(v) => setQ(v)}
            searchPlaceholder="Search by Booking ID…"
          />
        </div>

        <div className="sa-card">
          {loading ? (
            <div className="sa-placeholder" style={{ height: 120 }}>
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="sa-empty">No payments found.</div>
          ) : (
            <div className="sa-table-wrap">
              <table className={`sa-table ${isVenueAdmin ? "va-admin" : ""}`}>
                <thead>
                  <tr>
                    <th style={{ width: 64 }}>S.N.</th>
                    <th>Booking ID</th>
                    <th>Venue</th>
                    <th>User</th>

                    <th className="col-money">Slot Value</th>
                    <th className="col-money">Commission %</th>
                    <th className="col-money">Commission Amount</th>

                    {!isVenueAdmin && <th className="col-money">Gateway Fee</th>}
                    {!isVenueAdmin && <th>Promo</th>}
                    {!isVenueAdmin && <th className="col-money">User Paid</th>}
                    {!isVenueAdmin && <th className="col-money">Platform Net</th>}

                    <th className="col-money">Venue Settlement</th>
                    <th className="col-status">Status</th>

                  </tr>
                </thead>

                <tbody>
                  {items.map((p, i) => {
                    const sn = (page - 1) * limit + i + 1;
                    return (
                      <tr key={p._id}>
                        <td>{sn}</td>
                        <td>{p.bookingId}</td>
                        <td>{p.venueName || "—"}</td>
                        <td>{p.userName || "—"}</td>

                        <td className="col-money">{money(slotValue(p))}</td>
                        <td className="col-money">{percent(commissionPercent(p))}</td>
                        <td className="col-money">{money(p.commissionAmount)}</td>

                        {!isVenueAdmin && <td className="col-money">{money(p.gatewayFee)}</td>}
                        {!isVenueAdmin && <td>{promoCell(p)}</td>}
                        {!isVenueAdmin && <td className="col-money">{money(p.userPaid ?? 0)}</td>}
                        {!isVenueAdmin && <td className="col-money">{money(platformNet(p))}</td>}

                        <td className="col-money">{money(p.venueSettlement)}</td>

                        <td
                          className={`col-status ${p.paymentStatus === "completed"
                              ? "sa-status-success"
                              : p.paymentStatus === "failed"
                                ? "sa-status-danger"
                                : "sa-status-warn"
                            }`}
                        >
                          {p.paymentStatus}
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Paginator
                page={page}
                totalPages={pages}
                onPage={(p) => {
  setPage(p);
  load({ page: p });
}}
                pageSize={limit}
                onPageSizeChange={(n) => {
                  setLimit(n);
                  setPage(1);
                  load({ page: 1, limit: n });
                }}
                pageSizeOptions={[10, 20, 50, 100]}
              />

              <div className="sa-gray" style={{ marginTop: 8 }}>
                Total: {total}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = `
  .payments-skin {
    --pad: 14px;
    --gap: 12px;
    --border: #e6e8ec;
    --muted: #6b7280;
    --bg-subtle: #fafbff;
    --shadow: 0 4px 12px rgba(0,0,0,0.05);
    box-sizing: border-box;
  }
    .payments-skin *, .payments-skin *::before, .payments-skin *::after{
  box-sizing: border-box;
}

  .payments-skin .sa-row.sa-wrap{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(180px,1fr));
  gap:var(--gap);
  align-items:end;
  min-width: 0;
}
  .payments-skin .sa-row.sa-wrap .sa-actions{
    display:flex;
    gap:var(--gap);
    align-items:center;
    justify-content:flex-start;
  }
.payments-skin select,
.payments-skin input[type="text"],
.payments-skin input[type="search"],
.payments-skin input[type="date"],
.payments-skin input[type="month"]{
  height:40px;
  padding:8px 12px;
  border:1px solid var(--border);
  border-radius:10px;
  outline:none;
  font-size:14px;
  width:100%;
  max-width:100%;
  display:block;

  min-width: 0;
  -webkit-appearance: none;
  appearance: none;
}
  .payments-skin select:focus,
  .payments-skin input:focus{
    box-shadow:0 0 0 3px var(--bg-subtle);
    border-color:#d5dcff;
  }
    .payments-skin .sa-card{ overflow: hidden; }

  .payments-skin .sa-kpis{
    display:flex;
    flex-wrap:wrap;
    gap:16px;
    margin:18px 0;
  }
  .payments-skin .sa-kpi{
    background:#fff;
    border:1px solid var(--border);
    border-radius:12px;
    padding:16px;
    box-shadow:var(--shadow);
    flex:1 1 220px;
    min-width:180px;
  }
  .payments-skin .sa-kpi-title{
    color:var(--muted);
    font-size:13px;
    margin-bottom:6px;
  }
  .payments-skin .sa-kpi-value{
    font-size:20px;
    font-weight:700;
  }

  .payments-skin .table-toolbar{ margin-bottom:12px; }

  .payments-skin .sa-table-wrap{
    border:1px solid var(--border);
    border-radius:12px;
    overflow: auto;
  }
  .payments-skin .sa-table{
    width:100%;
    border-collapse:separate;
    border-spacing:0;
  }
  .payments-skin .sa-table thead th{
    background:var(--bg-subtle);
    font-size:13px;
    font-weight:600;
    color:#333;
    padding:12px 14px;
    border-bottom:1px solid var(--border);
  }
  .payments-skin .sa-table tbody td{
    padding:12px 14px;
    border-bottom:1px solid #f1f2f4;
    vertical-align:middle;
  }
  .payments-skin .sa-table tbody tr:hover{ background:#fcfdff; }
/* Money columns: consistent even when some columns are hidden */
.payments-skin .sa-table .col-money{
  text-align:right;
  white-space:nowrap;
}

/* Status column stays compact */
.payments-skin .sa-table .col-status{
  white-space:nowrap;
  text-transform:capitalize;
}


  .payments-skin .sa-status-success{ color:#28a745; font-weight:600; text-transform:capitalize; }
  .payments-skin .sa-status-danger{ color:#dc3545; font-weight:600; text-transform:capitalize; }
  .payments-skin .sa-status-warn{ color:#ffb200; font-weight:600; text-transform:capitalize; }

  .payments-skin .sa-actions .sa-btn,
  .payments-skin .sa-actions .sa-btn-outline{
    height:40px;
    padding:8px 12px;
    border-radius:10px;
    display:inline-flex;
    align-items:center;
    gap:8px;
  }
  .payments-skin .sa-actions .sa-btn-outline .lucide{ width:16px; height:16px; }

  .payments-skin .sa-gray{ margin-top:10px; }

  .payments-skin .sa-pager{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    padding:10px 12px;
    flex-wrap:wrap;
  }
  .payments-skin .sa-pager > :first-child{
    max-width:100%;
    overflow-x:auto;
    -webkit-overflow-scrolling:touch;
  }
  .payments-skin .sa-pager .sa-gray{ white-space:nowrap; }

    /* ✅ Critical: allow grid children to shrink on iOS (prevents date/month overflow) */
.payments-skin .sa-row.sa-wrap > * { min-width: 0; }
.payments-skin .sa-field { min-width: 0; }

  @media (max-width: 820px){
    .payments-skin .sa-row.sa-wrap { grid-template-columns: 1fr !important; }
    .payments-skin .sa-row.sa-wrap .sa-actions {
      grid-column: 1 / -1 !important;
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      gap: var(--gap);
    }
    .payments-skin .sa-row.sa-wrap .sa-actions .sa-btn,
    .payments-skin .sa-row.sa-wrap .sa-actions .sa-btn-outline {
      width: 100%;
      justify-content: center;
    }
    .payments-skin .sa-kpis { flex-direction: column; }
    /* ✅ Mobile KPI cards compact */
.payments-skin .sa-kpis{
  gap: 10px;
  margin: 12px 0;
}

.payments-skin .sa-kpi{
  padding: 12px;
  min-width: 0;
  flex: 0 0 auto;
}

.payments-skin .sa-kpi-title{
  font-size: 12px;
  margin-bottom: 4px;
}

.payments-skin .sa-kpi-value{
  font-size: 18px;
  line-height: 1.2;
}

    .payments-skin .sa-table-wrap {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
    .payments-skin .sa-table { min-width: 1100px; }
    .payments-skin .sa-table.va-admin { min-width: 720px; }
  }
`;

export default PaymentsList;
